import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js'
import { clockLabelSinceNoon, formatCalendarDay, formatDuration, type NightBar } from 'mormaii-web-presentation'

import { PAD } from './charts.axis'
import { useWidth } from './use-width'
import Css from './charts.module.scss'

/**
 * Os dois graficos de sono no formato do app Saude da Apple (B-41 e B-39; os prints de
 * referencia estao em `demandas/2026-10-14/`).
 *
 * - `DurationBars`, no painel da EMPRESA: uma barra por noite com a duracao media, a media
 *   do periodo como linha e a META media tracejada. Barra que ficou abaixo da meta muda de
 *   cor - e o que o relato 1 pediu.
 * - `SleepNights`, na FICHA: uma barra por noite, do deitar ao acordar, com o eixo em hora
 *   do relogio e a barra listrada pelas fases. O detalhe de cada noite aparece ao passar o
 *   ponteiro, sem clique (relato 14).
 *
 * Nada de media movel: a suavizacao da Apple e a linha da media sobre as barras.
 */

/** As cores das fases - as do app e do relatorio de sono, e a de "acordado" do app Saude. */
export const STAGE_COLORS: Record<number, string> = {
    0: '#F2994A',
    1: '#34E0D2',
    2: '#8FD9F2',
    3: '#4A9FE7'
}

const HOUR = 3600

export function DurationBars(props: {
    points: { time: number; value: number }[]
    goal?: number
    height?: number
}): JSX.Element {
    const [width, observe] = useWidth()
    const [hover, setHover] = createSignal<number | undefined>()
    const height = () => props.height ?? 320

    const average = createMemo(() =>
        props.points.length ? props.points.reduce((sum, p) => sum + p.value, 0) / props.points.length : undefined
    )

    /** O topo da escala: a maior noite ou a meta, o que for maior, arredondado em horas. */
    const top = createMemo(() => {
        const highest = Math.max(0, ...props.points.map((p) => p.value), props.goal ?? 0)
        return Math.max(HOUR, Math.ceil((highest * 1.1) / HOUR) * HOUR)
    })

    const plotWidth = () => Math.max(0, width() - PAD.left - PAD.right)
    const plotHeight = () => Math.max(0, height() - PAD.top - PAD.bottom)
    const y = (seconds: number) => PAD.top + plotHeight() - (seconds / top()) * plotHeight()
    const slot = () => plotWidth() / Math.max(1, props.points.length)

    /** Marcas em horas redondas - de 2 em 2, ou de hora em hora quando a escala e curta. */
    const ticks = createMemo(() => {
        const step = top() > 8 * HOUR ? 2 * HOUR : HOUR
        return Array.from({ length: Math.floor(top() / step) + 1 }, (_, i) => i * step)
    })

    /** Quatro datas no maximo embaixo, para nao encavalar. */
    const labelEvery = () => Math.max(1, Math.ceil(props.points.length / 7))

    let host!: HTMLDivElement
    createEffect(() => observe(host))

    return (
        <div ref={host} class={Css.frame} style={{ height: `${height()}px` }}>
            <Show
                when={props.points.length > 0 && width() > 0}
                fallback={<p class={Css.empty}>Sem medição no período</p>}
            >
                <svg width={width()} height={height()} class={Css.canvas} onPointerLeave={() => setHover(undefined)}>
                    <For each={ticks()}>
                        {(tick) => (
                            <>
                                <line
                                    x1={PAD.left}
                                    x2={PAD.left + plotWidth()}
                                    y1={y(tick)}
                                    y2={y(tick)}
                                    stroke="var(--line-2)"
                                    stroke-dasharray="3 3"
                                />
                                <text
                                    x={PAD.left - 10}
                                    y={y(tick)}
                                    text-anchor="end"
                                    dominant-baseline="middle"
                                    class={Css.axisNumber}
                                >
                                    {`${tick / HOUR}h`}
                                </text>
                            </>
                        )}
                    </For>

                    <For each={props.points}>
                        {(point, index) => {
                            const below = () => props.goal !== undefined && point.value < props.goal
                            const barWidth = () => Math.max(2, slot() * 0.6)
                            const x = () => PAD.left + index() * slot() + (slot() - barWidth()) / 2
                            return (
                                <>
                                    <rect
                                        x={x()}
                                        y={y(point.value)}
                                        width={barWidth()}
                                        height={Math.max(0, PAD.top + plotHeight() - y(point.value))}
                                        rx="3"
                                        fill={below() ? 'var(--status-fair)' : 'var(--accent)'}
                                        fill-opacity={hover() === index() ? 1 : 0.8}
                                        onPointerEnter={() => setHover(index())}
                                    />
                                    <Show when={index() % labelEvery() === 0}>
                                        <text
                                            x={x() + barWidth() / 2}
                                            y={height() - 8}
                                            text-anchor="middle"
                                            class={Css.axis}
                                        >
                                            {formatCalendarDay(point.time).slice(0, 5)}
                                        </text>
                                    </Show>
                                </>
                            )
                        }}
                    </For>

                    {/* A MEDIA do periodo: a linha sobre as barras, como no app Saude. */}
                    <Show when={average()}>
                        {(value) => (
                            <line
                                x1={PAD.left}
                                x2={PAD.left + plotWidth()}
                                y1={y(value())}
                                y2={y(value())}
                                stroke="var(--text)"
                                stroke-width="1.5"
                            />
                        )}
                    </Show>

                    {/* A META, tracejada. */}
                    <Show when={props.goal}>
                        {(goal) => (
                            <line
                                x1={PAD.left}
                                x2={PAD.left + plotWidth()}
                                y1={y(goal())}
                                y2={y(goal())}
                                stroke="var(--status-good)"
                                stroke-width="2"
                                stroke-dasharray="6 4"
                            />
                        )}
                    </Show>
                </svg>

                <Show when={hover() !== undefined && props.points[hover()!]}>
                    {(point) => (
                        <div
                            class={Css.tooltip}
                            style={{
                                left: `${Math.min(Math.max(PAD.left + (hover()! + 0.5) * slot(), 70), width() - 70)}px`,
                                top: `${y(point().value) - 10}px`
                            }}
                        >
                            <p class={Css.tooltipTime}>{formatCalendarDay(point().time)}</p>
                            <p class={Css.tooltipValue}>{formatDuration(point().value)}</p>
                        </div>
                    )}
                </Show>
            </Show>

            <Show when={props.points.length > 0}>
                <div class={Css.sleepLegend}>
                    <span class={Css.sleepLegendItem}>
                        <span class={Css.sleepLine} style={{ 'border-color': 'var(--text)' }} />
                        Média do período {average() !== undefined ? formatDuration(average()!) : ''}
                    </span>
                    <Show when={props.goal}>
                        <span class={Css.sleepLegendItem}>
                            <span
                                class={Css.sleepLine}
                                style={{ 'border-color': 'var(--status-good)', 'border-top-style': 'dashed' }}
                            />
                            Meta {formatDuration(props.goal!)}
                        </span>
                        <span class={Css.sleepLegendItem}>
                            <span class={Css.sleepSwatch} style={{ 'background-color': 'var(--status-fair)' }} />
                            Abaixo da meta
                        </span>
                    </Show>
                </div>
            </Show>
        </div>
    )
}

export function SleepNights(props: { bars: NightBar[]; summary?: string[]; height?: number }): JSX.Element {
    const [width, observe] = useWidth()
    const [hover, setHover] = createSignal<number | undefined>()
    const height = () => props.height ?? 320

    /** A janela do eixo em minutos desde o meio-dia, de 2 em 2 horas. */
    const range = createMemo(() => {
        const from = Math.min(...props.bars.map((bar) => bar.from))
        const to = Math.max(...props.bars.map((bar) => bar.to))
        return { from: Math.floor(from / 120) * 120, to: Math.ceil(to / 120) * 120 }
    })

    const plotWidth = () => Math.max(0, width() - PAD.left - PAD.right)
    const plotHeight = () => Math.max(0, height() - PAD.top - PAD.bottom)
    // Mais cedo EM CIMA, como no app Saude: a noite desce do deitar ao acordar.
    const y = (minutes: number) =>
        PAD.top + ((minutes - range().from) / Math.max(1, range().to - range().from)) * plotHeight()
    const slot = () => plotWidth() / Math.max(1, props.bars.length)

    const ticks = createMemo(() => {
        const out: number[] = []
        for (let m = range().from; m <= range().to; m += 120) out.push(m)
        return out
    })

    const labelEvery = () => Math.max(1, Math.ceil(props.bars.length / 7))

    let host!: HTMLDivElement
    createEffect(() => observe(host))

    return (
        <div class={Css.nightsBox}>
            <Show when={(props.summary ?? []).length > 0}>
                <p class={Css.nightsSummary}>{props.summary!.join(' · ')}</p>
            </Show>

            <div ref={host} class={Css.frame} style={{ height: `${height()}px` }}>
                <Show
                    when={props.bars.length > 0 && width() > 0}
                    fallback={<p class={Css.empty}>Sem noite registrada no período</p>}
                >
                    <svg
                        width={width()}
                        height={height()}
                        class={Css.canvas}
                        onPointerLeave={() => setHover(undefined)}
                    >
                        <For each={ticks()}>
                            {(tick) => (
                                <>
                                    <line
                                        x1={PAD.left}
                                        x2={PAD.left + plotWidth()}
                                        y1={y(tick)}
                                        y2={y(tick)}
                                        stroke="var(--line-2)"
                                        stroke-dasharray="3 3"
                                    />
                                    <text
                                        x={PAD.left - 10}
                                        y={y(tick)}
                                        text-anchor="end"
                                        dominant-baseline="middle"
                                        class={Css.axisNumber}
                                    >
                                        {clockLabelSinceNoon(tick)}
                                    </text>
                                </>
                            )}
                        </For>

                        <For each={props.bars}>
                            {(bar, index) => {
                                const barWidth = () => Math.max(3, Math.min(28, slot() * 0.55))
                                const x = () => PAD.left + index() * slot() + (slot() - barWidth()) / 2
                                return (
                                    <g
                                        onPointerEnter={() => setHover(index())}
                                        opacity={hover() === undefined || hover() === index() ? 1 : 0.55}
                                    >
                                        {/* A noite inteira, por baixo: o que nao tem fase fica nesta cor. */}
                                        <rect
                                            x={x()}
                                            y={y(bar.from)}
                                            width={barWidth()}
                                            height={Math.max(1, y(bar.to) - y(bar.from))}
                                            rx="3"
                                            fill="var(--line)"
                                        />
                                        <For each={bar.segments}>
                                            {(segment) => (
                                                <rect
                                                    x={x()}
                                                    y={y(segment.from)}
                                                    width={barWidth()}
                                                    height={Math.max(0.5, y(segment.to) - y(segment.from))}
                                                    fill={STAGE_COLORS[segment.stage] ?? 'var(--line)'}
                                                />
                                            )}
                                        </For>
                                        <Show when={index() % labelEvery() === 0}>
                                            <text
                                                x={x() + barWidth() / 2}
                                                y={height() - 8}
                                                text-anchor="middle"
                                                class={Css.axis}
                                            >
                                                {bar.label}
                                            </text>
                                        </Show>
                                    </g>
                                )
                            }}
                        </For>
                    </svg>

                    <Show when={hover() !== undefined && props.bars[hover()!]}>
                        {(bar) => (
                            <div
                                class={Css.tooltip}
                                style={{
                                    left: `${Math.min(Math.max(PAD.left + (hover()! + 0.5) * slot(), 90), width() - 90)}px`,
                                    top: `${y(bar().from) - 10}px`
                                }}
                            >
                                <p class={Css.tooltipTime}>Noite de {bar().label}</p>
                                <For each={bar().details}>{(line) => <p class={Css.tooltipValue}>{line}</p>}</For>
                            </div>
                        )}
                    </Show>
                </Show>
            </div>

            <div class={Css.sleepLegend}>
                <For
                    each={
                        [
                            [1, 'REM'],
                            [2, 'Leve'],
                            [3, 'Profundo'],
                            [0, 'Acordado']
                        ] as const
                    }
                >
                    {([stage, label]) => (
                        <span class={Css.sleepLegendItem}>
                            <span class={Css.sleepSwatch} style={{ 'background-color': STAGE_COLORS[stage] }} />
                            {label}
                        </span>
                    )}
                </For>
            </div>
        </div>
    )
}
